// src/pages/psa/components/psa-cards-display/PSACardsDisplay.js
import { useState, useRef, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Timestamp } from 'firebase/firestore';
import { useDispatch } from 'react-redux';
import { PSA_STATUSES, GRADING_SERVICES_WITH_PRICES, formatServiceWithPrice, PSA_GRADES, GRADING_SERVICES, GRADING_PRICES } from '../../../../utils/psa/psa.constants';
import { setSystemNotification } from '../../../../store/system/system.action';
import LoadingIcon from '../../../../components/loading-icon/LoadingIcon';
import CardAttribute from '../../../../components/card-attribute/CardAttribute';
import InputDropdown from '../../../../components/input-dropdown/InputDropdown';
import InputBar from '../../../../components/input-bar/InputBar';
import PrimaryBtn from '../../../../components/btns/primary-btn/PrimaryBtn';
import SecondaryBtn from '../../../../components/btns/secondary-btn/SecondaryBtn';

// styles - using ChecklistItemDisplay styles for consistency
import {
  ChecklistItemDisplayContainer,
  CardDetails,
  TopCardContent,
  LeftCardContent,
  CardNumber,
  QuantityControlsContainer,
  QtyContent,
  CardHeader as ChecklistCardHeader,
  AttributesContainer
} from '../../../../components/checklist-item-display/ChecklistItemDisplay.styles';

// styles - PSA specific
import {

  PSACardsDisplayContainer,
  StatusHeader,
  StatusHeaderContainer,
  StatusItem,
  StatusName,
  StatusCount,
  CardsDisplayContainer,
  CardsContainer,
  EmptyState,
  ExpandedSection,
  SubmissionExpandedContainer,
  SubmissionFormColumn,
  SubmissionCardsColumn,
  FormSection,
  FormRowContainer,
  FormRow,
  FormLabel,
  ButtonGroup,
  SubmissionGroup,
  SubmissionHeader,
  SubmissionHeaderContent,
  SubmissionInfo,
  SubmissionTitle,
  SubmissionID,
  SubmissionDetails,
  SubmissionActions,
  GradingDashboard,
  DashboardMetric,
  MetricLabel,
  MetricValue,
  UnassignedGroup,
  UnassignedHeader,
  UnassignedTitle
} from './PSACardsDisplay.styles';

export default function PSACardsDisplay({ psaCards, psaSubmissions = {}, isLoading, onUpdateCard, onDeleteCard, onUpdateSubmission, onDeleteSubmission, onAddCardToSubmission, onAssignCardToSubmission }) {
  const dispatch = useDispatch();
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [expandedSubmissionId, setExpandedSubmissionId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null); // null means show all
  const cardRefs = useRef({});
  const submissionRefs = useRef({});

  // Form state for expanded card
  const [formData, setFormData] = useState({
    status: '',
    grade: '',
    serviceLevel: '',
    trackingNumber: '',
    notes: '',
    psaEventDropOff: false,
    shipToPSA: false,
    psaDropOffDateTime: ''
  });

  // Form state for expanded submission
  const [submissionFormData, setSubmissionFormData] = useState({
    psaSubmissionID: '',
    status: '',
    serviceLevel: '',
    trackingNumber: '',
    notes: '',
    psaEventDropOff: false,
    shipToPSA: false,
    psaDropOffDateTime: '',
    receivedDate: '',
    gradingStartDate: ''
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingSubmission, setIsSavingSubmission] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showDeleteSubmissionConfirm, setShowDeleteSubmissionConfirm] = useState(false);

  // Get cards array and organize by submission - must be before early returns for useEffect
  const cardsArray = Object.values(psaCards);

  // Group cards by submissionID
  const cardsBySubmission = cardsArray.reduce((acc, card) => {
    const submissionID = card.submissionID || 'unassigned';
    if (!acc[submissionID]) {
      acc[submissionID] = [];
    }
    acc[submissionID].push(card);
    return acc;
  }, {});

  // Organize submissions and unassigned cards
  const submissionGroups = Object.keys(psaSubmissions).map(submissionID => ({
    submissionID,
    submission: psaSubmissions[submissionID],
    cards: cardsBySubmission[submissionID] || []
  }));

  const unassignedCards = cardsBySubmission['unassigned'] || [];

  // For status filtering, we still need cardsByStatus for the status header
  const statusOrder = [PSA_STATUSES.DRAFT, ...Object.values(PSA_STATUSES).filter(s => s !== PSA_STATUSES.DRAFT)];
  const cardsByStatus = cardsArray.reduce((acc, card) => {
    // Use submission status if card is in a submission, otherwise use card status
    const submission = card.submissionID ? psaSubmissions[card.submissionID] : null;
    const status = submission?.status || card.status || PSA_STATUSES.DRAFT;
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(card);
    return acc;
  }, {});

  // Set initial selected status to first status with cards/submissions, or first status if none
  useEffect(() => {
    if (selectedStatus === null) {
      // Check for status with cards or submissions
      const firstStatusWithContent = statusOrder.find(status => {
        const hasCards = (cardsByStatus[status] || []).length > 0;
        const hasSubmissions = submissionGroups.some(group => {
          const submissionStatus = group.submission?.status || PSA_STATUSES.DRAFT;
          return submissionStatus === status;
        });
        return hasCards || hasSubmissions;
      });
      
      if (firstStatusWithContent) {
        setSelectedStatus(firstStatusWithContent);
      } else if (statusOrder.length > 0) {
        setSelectedStatus(statusOrder[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsArray.length, Object.keys(psaSubmissions).length]);

  // Initialize form data when card is expanded
  useEffect(() => {
    if (expandedCardId && psaCards[expandedCardId]) {
      const card = psaCards[expandedCardId];
      const gradeKey = card.grade ? `PSA ${card.grade}` : '';
      const status = card.status || PSA_STATUSES.DRAFT;

      // Format date/time for datetime-local input (YYYY-MM-DDTHH:mm)
      let formattedDateTime = '';
      if (card.psaDropOffDateTime) {
        const date = card.psaDropOffDateTime.toDate ? card.psaDropOffDateTime.toDate() : new Date(card.psaDropOffDateTime);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        formattedDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      setFormData({
        status: status,
        grade: gradeKey,
        serviceLevel: card.serviceLevel || '',
        trackingNumber: card.trackingNumber || '',
        notes: card.notes || '',
        psaEventDropOff: status === PSA_STATUSES.PSA_DROP_OFF || card.psaEventDropOff || false,
        shipToPSA: status === PSA_STATUSES.SHIPPED_TO_PSA || card.shipToPSA || false,
        psaDropOffDateTime: formattedDateTime
      });
      setShowDeleteConfirm(false);
      setIsSaving(false);
      setIsDeleting(false);
    }
  }, [expandedCardId, psaCards]);

  // Sync checkboxes when status changes
  useEffect(() => {
    if (expandedCardId) {
      setFormData(prev => {
        const newPsaEventDropOff = prev.status === PSA_STATUSES.PSA_DROP_OFF;
        const newShipToPSA = prev.status === PSA_STATUSES.SHIPPED_TO_PSA;

        // Only update if status changed
        if (prev.psaEventDropOff !== newPsaEventDropOff || prev.shipToPSA !== newShipToPSA) {
          return {
            ...prev,
            psaEventDropOff: newPsaEventDropOff,
            shipToPSA: newShipToPSA
          };
        }
        return prev;
      });
    }
  }, [formData.status, expandedCardId]);

  // Click outside handler for expanded cards and submissions
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle expanded card
      if (expandedCardId) {
        const cardRef = cardRefs.current[expandedCardId];
        if (cardRef && !cardRef.contains(event.target)) {
          setExpandedCardId(null);
          setShowDeleteConfirm(false);
          setShowRemoveConfirm(false);
        }
      }

      // Handle expanded submission
      if (expandedSubmissionId) {
        const submissionRef = submissionRefs.current[expandedSubmissionId];
        if (submissionRef && !submissionRef.contains(event.target) && !event.target.closest('.submission-edit')) {
          setExpandedSubmissionId(null);
          setShowDeleteSubmissionConfirm(false);
        }
      }
    };

    if (expandedCardId || expandedSubmissionId) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [expandedCardId, expandedSubmissionId]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px'
      }}>
        <LoadingIcon />
      </div>
    );
  }

  if (cardsArray.length === 0) {
    return (
      <EmptyState>
        <p>No PSA cards tracked yet. Add a new grading to get started!</p>
      </EmptyState>
    );
  }

  // Calculate counts for each status
  // Calculate status counts including both cards and empty submissions
  const statusCounts = statusOrder.reduce((acc, status) => {
    // Count cards with this status
    const cardCount = (cardsByStatus[status] || []).length;
    
    // Count empty submissions with this status
    const emptySubmissionCount = submissionGroups.filter(group => {
      const submissionStatus = group.submission?.status || PSA_STATUSES.DRAFT;
      return submissionStatus === status && group.cards.length === 0;
    }).length;
    
    acc[status] = cardCount + emptySubmissionCount;
    return acc;
  }, {});

  const handleCardClick = (card, e) => {
    // Don't expand if clicking on status dropdown
    if (e.target.closest('.status-dropdown')) {
      return;
    }

    // Toggle expansion
    if (expandedCardId === card.id) {
      setExpandedCardId(null);
      setShowDeleteConfirm(false);
      setShowRemoveConfirm(false);
    } else {
      setExpandedCardId(card.id);
      setShowDeleteConfirm(false);
      setShowRemoveConfirm(false);
    }
  };

  const handleStatusChange = async (cardId, newStatus) => {
    if (!onUpdateCard) return;

    try {
      const status = newStatus || PSA_STATUSES.DRAFT;
      const updates = {
        status: status,
        // Sync checkboxes based on status
        psaEventDropOff: status === PSA_STATUSES.PSA_DROP_OFF,
        shipToPSA: status === PSA_STATUSES.SHIPPED_TO_PSA
      };

      // If card is expanded, update formData to reflect status change
      if (expandedCardId === cardId) {
        setFormData(prev => ({
          ...prev,
          status: status,
          psaEventDropOff: status === PSA_STATUSES.PSA_DROP_OFF,
          shipToPSA: status === PSA_STATUSES.SHIPPED_TO_PSA
        }));
      }

      await onUpdateCard(cardId, updates);
    } catch (error) {
      console.error('Error updating card status:', error);
    }
  };

  const handleCancel = () => {
    setExpandedCardId(null);
    setShowDeleteConfirm(false);
    setShowRemoveConfirm(false);
  };

  const handleSave = async () => {
    if (!expandedCardId || !onUpdateCard) return;

    setIsSaving(true);
    try {
      let status = formData.status && formData.status.trim() ? formData.status : PSA_STATUSES.DRAFT;

      // Auto-update status if Draft has date/time or tracking number
      if (status === PSA_STATUSES.DRAFT) {
        if (formData.psaDropOffDateTime && formData.psaDropOffDateTime.trim()) {
          status = PSA_STATUSES.PSA_DROP_OFF;
        } else if (formData.trackingNumber && formData.trackingNumber.trim()) {
          status = PSA_STATUSES.SHIPPED_TO_PSA;
        }
      }

      // Convert datetime string to Firestore Timestamp
      let psaDropOffDateTime = null;
      if (formData.psaDropOffDateTime && formData.psaDropOffDateTime.trim()) {
        const date = new Date(formData.psaDropOffDateTime);
        if (!isNaN(date.getTime())) {
          psaDropOffDateTime = Timestamp.fromDate(date);
        }
      }

      const updates = {
        status: status,
        grade: formData.grade ? formData.grade.replace(/\D/g, '') : null,
        serviceLevel: formData.serviceLevel || null,
        trackingNumber: formData.trackingNumber.trim() || null,
        notes: formData.notes.trim() || '',
        psaEventDropOff: status === PSA_STATUSES.PSA_DROP_OFF || formData.psaEventDropOff || false,
        shipToPSA: status === PSA_STATUSES.SHIPPED_TO_PSA || formData.shipToPSA || false,
        psaDropOffDateTime: psaDropOffDateTime
      };

      await onUpdateCard(expandedCardId, updates);
      setExpandedCardId(null);
      setShowDeleteConfirm(false);
      setShowRemoveConfirm(false);
    } catch (error) {
      console.error('Error saving PSA card updates:', error);
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!expandedCardId || !onDeleteCard) return;

    setIsDeleting(true);
    try {
      await onDeleteCard(expandedCardId);
      setExpandedCardId(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting PSA card:', error);
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleRemoveFromSubmissionClick = () => {
    setShowRemoveConfirm(true);
  };

  const handleRemoveFromSubmissionConfirm = async () => {
    if (!expandedCardId || !onUpdateCard) return;
    
    setIsDeleting(true);
    try {
      await onUpdateCard(expandedCardId, { submissionID: null });
      setExpandedCardId(null);
      setShowRemoveConfirm(false);
    } catch (error) {
      console.error('Error removing card from submission:', error);
      setIsDeleting(false);
    }
  };

  const handleRemoveFromSubmissionCancel = () => {
    setShowRemoveConfirm(false);
  };

  const handleDeleteSubmissionClick = () => {
    setShowDeleteSubmissionConfirm(true);
  };

  const handleDeleteSubmissionConfirm = async () => {
    if (!expandedSubmissionId || !onDeleteSubmission) return;
    
    setIsDeleting(true);
    try {
      await onDeleteSubmission(expandedSubmissionId);
      setExpandedSubmissionId(null);
      setShowDeleteSubmissionConfirm(false);
    } catch (error) {
      console.error('Error deleting submission:', error);
      setIsDeleting(false);
    }
  };

  const handleDeleteSubmissionCancel = () => {
    setShowDeleteSubmissionConfirm(false);
  };

  // Submission handlers
  const handleSubmissionClick = (submissionId, e) => {
    if (e.target.closest('.submission-actions') || e.target.closest('.submission-edit')) {
      return;
    }

    if (expandedSubmissionId === submissionId) {
      setExpandedSubmissionId(null);
      setIsSavingSubmission(false);
    } else {
      setExpandedSubmissionId(submissionId);
      setIsSavingSubmission(false);
      // Initialize submission form data
      const submission = psaSubmissions[submissionId];
      if (submission) {
        let formattedDateTime = '';
        if (submission.psaDropOffDateTime) {
          const date = submission.psaDropOffDateTime.toDate ? submission.psaDropOffDateTime.toDate() : new Date(submission.psaDropOffDateTime);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          formattedDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
        }

        let formattedReceivedDate = '';
        if (submission.receivedDate) {
          const date = submission.receivedDate.toDate ? submission.receivedDate.toDate() : new Date(submission.receivedDate);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          formattedReceivedDate = `${year}-${month}-${day}`;
        }

        let formattedGradingStartDate = '';
        if (submission.gradingStartDate) {
          const date = submission.gradingStartDate.toDate ? submission.gradingStartDate.toDate() : new Date(submission.gradingStartDate);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          formattedGradingStartDate = `${year}-${month}-${day}`;
        }

        setSubmissionFormData({
          psaSubmissionID: submission.psaSubmissionID || '',
          status: submission.status || PSA_STATUSES.DRAFT,
          serviceLevel: submission.serviceLevel || '',
          trackingNumber: submission.trackingNumber || '',
          notes: submission.notes || '',
          psaEventDropOff: submission.psaEventDropOff || false,
          shipToPSA: submission.shipToPSA || false,
          psaDropOffDateTime: formattedDateTime,
          receivedDate: formattedReceivedDate,
          gradingStartDate: formattedGradingStartDate
        });
      }
    }
  };

  const handleSaveSubmission = async () => {
    if (!expandedSubmissionId || !onUpdateSubmission) return;

    setIsSavingSubmission(true);
    try {
      let status = submissionFormData.status && submissionFormData.status.trim() ? submissionFormData.status : PSA_STATUSES.DRAFT;

      // Auto-update status if Draft has date/time or tracking number
      if (status === PSA_STATUSES.DRAFT) {
        if (submissionFormData.psaDropOffDateTime && submissionFormData.psaDropOffDateTime.trim()) {
          status = PSA_STATUSES.PSA_DROP_OFF;
        } else if (submissionFormData.trackingNumber && submissionFormData.trackingNumber.trim()) {
          status = PSA_STATUSES.SHIPPED_TO_PSA;
        }
      }

      // Validate that all cards have grades before allowing Complete status
      if (status === PSA_STATUSES.COMPLETE) {
        const submissionCards = cardsBySubmission[expandedSubmissionId] || [];
        
        // Check if submission has no cards
        if (submissionCards.length === 0) {
          dispatch(setSystemNotification('All cards must have a grade to mark as Complete'));
          setIsSavingSubmission(false);
          return; // Prevent status update
        }
        
        // Check if any cards are missing grades
        const cardsWithoutGrades = submissionCards.filter(card => !card.grade || !card.grade.trim());
        if (cardsWithoutGrades.length > 0) {
          dispatch(setSystemNotification('All cards must have a grade to mark as Complete'));
          setIsSavingSubmission(false);
          return; // Prevent status update
        }
      }

      // Convert datetime string to Firestore Timestamp
      let psaDropOffDateTime = null;
      if (submissionFormData.psaDropOffDateTime && submissionFormData.psaDropOffDateTime.trim()) {
        const date = new Date(submissionFormData.psaDropOffDateTime);
        if (!isNaN(date.getTime())) {
          psaDropOffDateTime = Timestamp.fromDate(date);
        }
      }

      // Convert received date string to Firestore Timestamp
      let receivedDate = null;
      if (submissionFormData.receivedDate && submissionFormData.receivedDate.trim()) {
        const date = new Date(submissionFormData.receivedDate);
        if (!isNaN(date.getTime())) {
          // Set to start of day for consistent date handling
          date.setHours(0, 0, 0, 0);
          receivedDate = Timestamp.fromDate(date);
        }
      }

      // Convert grading start date string to Firestore Timestamp
      let gradingStartDate = null;
      if (submissionFormData.gradingStartDate && submissionFormData.gradingStartDate.trim()) {
        const date = new Date(submissionFormData.gradingStartDate);
        if (!isNaN(date.getTime())) {
          // Set to start of day for consistent date handling
          date.setHours(0, 0, 0, 0);
          gradingStartDate = Timestamp.fromDate(date);
        }
      }

      const updates = {
        psaSubmissionID: submissionFormData.psaSubmissionID.trim() || '',
        status: status,
        serviceLevel: submissionFormData.serviceLevel || null,
        trackingNumber: submissionFormData.trackingNumber.trim() || null,
        notes: submissionFormData.notes.trim() || '',
        psaEventDropOff: status === PSA_STATUSES.PSA_DROP_OFF,
        shipToPSA: status === PSA_STATUSES.SHIPPED_TO_PSA,
        psaDropOffDateTime: psaDropOffDateTime,
        receivedDate: receivedDate,
        gradingStartDate: gradingStartDate
      };

      await onUpdateSubmission(expandedSubmissionId, updates);
      setExpandedSubmissionId(null);
      setIsSavingSubmission(false);
      setShowDeleteSubmissionConfirm(false);
    } catch (error) {
      console.error('Error saving submission updates:', error);
      setIsSavingSubmission(false);
    }
  };

  // Get status and grade options
  const statusOptions = ['', ...Object.values(PSA_STATUSES)];
  const gradeOptions = ['', ...Object.keys(PSA_GRADES)];

  const submissionServiceLevelOptions = submissionFormData.serviceLevel
    ? [
      formatServiceWithPrice(submissionFormData.serviceLevel),
      ...GRADING_SERVICES_WITH_PRICES.filter(s => !s.startsWith(submissionFormData.serviceLevel))
    ]
    : GRADING_SERVICES_WITH_PRICES;

  const renderCard = (card) => {
    const { cardName, cardNumber, cardAttributes, selectedVariation, cardPath } = card;
    const name = cardName || [];
    const series = cardPath?.series || '';
    const isExpanded = expandedCardId === card.id;

    return (
      <ChecklistItemDisplayContainer
        key={card.id}
        ref={(el) => {
          if (el) {
            cardRefs.current[card.id] = el;
          }
        }}
        className='panel-bg dropshadow'
        onClick={(e) => handleCardClick(card, e)}
        style={{ cursor: 'pointer' }}
      >
        <TopCardContent>
          <LeftCardContent>
            <CardNumber>
              <h4>{cardNumber}</h4>
            </CardNumber>
            <CardDetails>
              <ChecklistCardHeader>
                {name.map((playerName, index) => (
                  <span key={index}>
                    <h3>{playerName}</h3>
                    {index < name.length - 1 && ' • '}
                  </span>
                ))}
              </ChecklistCardHeader>
              {(cardAttributes?.length > 0 || selectedVariation || series) && (
                <AttributesContainer>
                  {series && (
                    <>
                      <CardAttribute text={series} />
                      {(cardAttributes?.length > 0 || selectedVariation) && <h4>•</h4>}
                    </>
                  )}
                  {cardAttributes?.length > 0 && cardAttributes.map(attribute => (
                    <CardAttribute key={attribute} text={attribute} />
                  ))}
                  {cardAttributes?.length > 0 && selectedVariation && <h4>•</h4>}
                  {selectedVariation && (
                    <CardAttribute
                      color={selectedVariation.cardColor || '#FFFFFF'}
                      text={
                        selectedVariation.cardSupply
                          ? `${selectedVariation.cardParallelName} (${selectedVariation.cardPrintNumber}/${selectedVariation.cardSupply})`
                          : selectedVariation.cardParallelName
                      }
                    />
                  )}
                </AttributesContainer>
              )}
            </CardDetails>
          </LeftCardContent>
          <QuantityControlsContainer>
            <QtyContent>
              {/* Grade dropdown - always visible */}
              <div
                className="grade-dropdown"
                onClick={(e) => e.stopPropagation()}
                style={{ minWidth: '180px' }}
              >
                <InputDropdown
                  size="compact"
                  id={`grade-inline-${card.id}`}
                  label="Grade"
                  value={card.grade ? `PSA ${card.grade}` : ''}
                  onChange={(value) => {
                    let gradeValue = null;
                    if (value) {
                      // Extract grade value: remove "PSA " prefix
                      gradeValue = value.replace(/^PSA\s+/, '') || null;
                    }
                    if (onUpdateCard) {
                      onUpdateCard(card.id, { grade: gradeValue });
                    }
                  }}
                  options={gradeOptions}
                  placeholder="Select grade"
                />
              </div>
              {/* Only show status dropdown for cards not in a submission */}
              {!card.submissionID && (
                <div
                  className="status-dropdown"
                  onClick={(e) => e.stopPropagation()}
                  style={{ minWidth: '180px' }}
                >
                  <InputDropdown
                    size="compact"
                    id={`status-inline-${card.id}`}
                    label="Status"
                    value={card.status || PSA_STATUSES.DRAFT}
                    onChange={(value) => handleStatusChange(card.id, value)}
                    options={statusOptions}
                    placeholder="Select status"
                  />
                </div>
              )}
              {/* Show assignment dropdown for unassigned cards */}
              {!card.submissionID && Object.keys(psaSubmissions).length > 0 && (
                <div
                  className="submission-assign-dropdown"
                  onClick={(e) => e.stopPropagation()}
                  style={{ minWidth: '200px' }}
                >
                  <InputDropdown
                    size="compact"
                    id={`assign-submission-${card.id}`}
                    label="Assign to Submission"
                    value=""
                    onChange={(value) => {
                      if (value && onAssignCardToSubmission) {
                        onAssignCardToSubmission(card.id, value);
                      }
                    }}
                    placeholder="Assign to submission"
                  >
                    {Object.keys(psaSubmissions).map(id => {
                      const sub = psaSubmissions[id];
                      const displayName = sub.psaSubmissionID || `Submission ${id.slice(0, 8)}`;
                      return (
                        <option key={id} value={id}>
                          {displayName}
                        </option>
                      );
                    })}
                  </InputDropdown>
                </div>
              )}
            </QtyContent>
          </QuantityControlsContainer>
        </TopCardContent>

        {/* Expanded editing section */}
        {isExpanded && (
          <AnimatePresence>
            <ExpandedSection
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              <FormSection>
                <FormRow>
                  <FormLabel>Notes</FormLabel>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add notes..."
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '12px',
                      borderRadius: '8px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#FFFFFF',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </FormRow>
              </FormSection>

              <ButtonGroup>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {!showDeleteConfirm && !showRemoveConfirm ? (
                    <>
                      <PrimaryBtn
                        type="critical"
                        title="Delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick();
                        }}
                        disabled={isSaving || isDeleting}
                      />
                      {card.submissionID && (
                        <PrimaryBtn
                          type="critical"
                          title="Remove from Submission"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFromSubmissionClick();
                          }}
                          disabled={isSaving || isDeleting}
                        />
                      )}
                    </>
                  ) : showDeleteConfirm ? (
                    <>
                      <PrimaryBtn
                        type="critical"
                        title={isDeleting ? 'Deleting...' : 'Confirm Delete'}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteConfirm();
                        }}
                        disabled={isDeleting}
                        isLoading={isDeleting}
                      />
                      <SecondaryBtn
                        title="CANCEL"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCancel();
                        }}
                        isDisabled={isDeleting}
                      />
                    </>
                  ) : (
                    <>
                      <PrimaryBtn
                        type="critical"
                        title={isDeleting ? 'Removing...' : 'Confirm Remove'}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFromSubmissionConfirm();
                        }}
                        disabled={isDeleting}
                        isLoading={isDeleting}
                      />
                      <SecondaryBtn
                        title="CANCEL"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFromSubmissionCancel();
                        }}
                        isDisabled={isDeleting}
                      />
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <PrimaryBtn
                    title={isSaving ? 'Saving...' : 'Save Changes'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSave();
                    }}
                    disabled={isSaving || isDeleting || showDeleteConfirm || showRemoveConfirm}
                  />
                  <SecondaryBtn
                    title="CANCEL"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancel();
                    }}
                    isDisabled={isSaving || isDeleting}
                  />
                </div>
              </ButtonGroup>
            </ExpandedSection>
          </AnimatePresence>
        )}
      </ChecklistItemDisplayContainer>
    );
  };

  return (
    <PSACardsDisplayContainer>
      <StatusHeader>
        <StatusHeaderContainer>
          {statusOrder.map((status) => (
            <StatusItem
              key={status}
              $isSelected={selectedStatus === status}
              onClick={() => setSelectedStatus(status)}
            >
              <StatusName>{status}</StatusName>
              <StatusCount>{statusCounts[status] || 0}</StatusCount>
            </StatusItem>
          ))}
        </StatusHeaderContainer>
      </StatusHeader>

      <CardsContainer>
        {selectedStatus && (() => {
          // Filter submissions by status
          const filteredSubmissions = submissionGroups.filter(group => {
            const submissionStatus = group.submission?.status || PSA_STATUSES.DRAFT;
            return submissionStatus === selectedStatus;
          });

          // Filter unassigned cards by status
          const filteredUnassigned = unassignedCards.filter(card => {
            const cardStatus = card.status || PSA_STATUSES.DRAFT;
            return cardStatus === selectedStatus;
          });

          if (filteredSubmissions.length === 0 && filteredUnassigned.length === 0) {
            return (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '16px'
              }}>
                No cards in {selectedStatus}
              </div>
            );
          }

          return (
            <CardsDisplayContainer>
              {/* Render submissions */}
              {filteredSubmissions.map(group => {
                const submission = group.submission;
                const isExpanded = expandedSubmissionId === group.submissionID;
                const submissionStatus = submission?.status || PSA_STATUSES.DRAFT;

                // Calculate grading metrics
                const cardsWithGrades = group.cards.filter(card => card.grade && card.grade.trim()).length;
                const totalCards = group.cards.length;
                const allCardsGraded = totalCards > 0 && cardsWithGrades === totalCards;
                
                // Calculate gem rate (PSA 10 percentage)
                const gemCards = group.cards.filter(card => card.grade && card.grade.trim() === '10').length;
                const gemRate = cardsWithGrades > 0 ? ((gemCards / cardsWithGrades) * 100).toFixed(1) : '0.0';
                
                // Calculate average grade
                const gradedCards = group.cards.filter(card => card.grade && card.grade.trim());
                let averageGrade = 'N/A';
                if (gradedCards.length > 0) {
                  const numericGrades = gradedCards
                    .map(card => {
                      const grade = card.grade.trim();
                      // Handle numeric grades (10, 9, etc.) and special cases (N0, AA)
                      if (grade === 'N0' || grade === 'AA') return null;
                      const numGrade = parseFloat(grade);
                      return isNaN(numGrade) ? null : numGrade;
                    })
                    .filter(grade => grade !== null);
                  
                  if (numericGrades.length > 0) {
                    const sum = numericGrades.reduce((acc, grade) => acc + grade, 0);
                    averageGrade = (sum / numericGrades.length).toFixed(2);
                  }
                }

                return (
                  <SubmissionGroup key={group.submissionID}>
                    <SubmissionHeader
                      ref={(el) => {
                        if (el) {
                          submissionRefs.current[group.submissionID] = el;
                        }
                      }}
                      onClick={(e) => handleSubmissionClick(group.submissionID, e)}
                      whileTap={{ scale: 0.99 }}
                    >
                      <SubmissionHeaderContent>
                        <SubmissionInfo>
                          <SubmissionTitle>
                            <SubmissionID>
                              PSA Submission {submission.psaSubmissionID || group.submissionID.slice(0, 8)}
                            </SubmissionID>
                          </SubmissionTitle>
                          <SubmissionDetails>
                            {submission.serviceLevel && (() => {
                              const pricePerCard = GRADING_PRICES[submission.serviceLevel];
                              const cardCount = group.cards.length;
                              const totalPrice = pricePerCard ? (pricePerCard * cardCount).toFixed(2) : '0.00';
                              const formattedPricePerCard = pricePerCard ? pricePerCard.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
                              const formattedTotalPrice = parseFloat(totalPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                              
                              return (
                                <DashboardMetric>
                                  <MetricLabel>{submission.serviceLevel}</MetricLabel>
                                  <MetricValue>${formattedPricePerCard} × {cardCount} = ${formattedTotalPrice}</MetricValue>
                                </DashboardMetric>
                              );
                            })()}
                            <GradingDashboard>
                              <DashboardMetric>
                                <MetricLabel>Progress</MetricLabel>
                                <MetricValue>{cardsWithGrades}/{totalCards}</MetricValue>
                              </DashboardMetric>
                              <DashboardMetric>
                                <MetricLabel>Gem Rate</MetricLabel>
                                <MetricValue>{gemRate}%</MetricValue>
                              </DashboardMetric>
                              <DashboardMetric>
                                <MetricLabel>Avg Grade</MetricLabel>
                                <MetricValue>{averageGrade}</MetricValue>
                              </DashboardMetric>
                            </GradingDashboard>
                            {submissionStatus === PSA_STATUSES.PSA_DROP_OFF && submission.psaDropOffDateTime && (() => {
                              const date = submission.psaDropOffDateTime.toDate ? submission.psaDropOffDateTime.toDate() : new Date(submission.psaDropOffDateTime);
                              const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                              const formattedTime = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                              return (
                                <DashboardMetric>
                                  <MetricLabel>Drop-Off</MetricLabel>
                                  <MetricValue>{formattedDate} {formattedTime}</MetricValue>
                                </DashboardMetric>
                              );
                            })()}
                            {submissionStatus === PSA_STATUSES.SHIPPED_TO_PSA && submission.trackingNumber && (
                              <DashboardMetric>
                                <MetricLabel>Tracking</MetricLabel>
                                <MetricValue>{submission.trackingNumber}</MetricValue>
                              </DashboardMetric>
                            )}
                            {submissionStatus === PSA_STATUSES.ORDER_ARRIVED && submission.receivedDate && (() => {
                              const date = submission.receivedDate.toDate ? submission.receivedDate.toDate() : new Date(submission.receivedDate);
                              const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              date.setHours(0, 0, 0, 0);
                              const diffTime = today - date;
                              const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                              const daysAgoText = diffDays === 0 ? 'Today' : diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
                              return (
                                <DashboardMetric>
                                  <MetricLabel>Received</MetricLabel>
                                  <MetricValue>{formattedDate} ({daysAgoText})</MetricValue>
                                </DashboardMetric>
                              );
                            })()}
                            {submissionStatus === PSA_STATUSES.GRADING_IN_PROGRESS && submission.gradingStartDate && (() => {
                              const date = submission.gradingStartDate.toDate ? submission.gradingStartDate.toDate() : new Date(submission.gradingStartDate);
                              const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              date.setHours(0, 0, 0, 0);
                              const diffTime = today - date;
                              const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                              const daysAgoText = diffDays === 0 ? 'Today' : diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
                              return (
                                <DashboardMetric>
                                  <MetricLabel>Grading Started</MetricLabel>
                                  <MetricValue>{formattedDate} ({daysAgoText})</MetricValue>
                                </DashboardMetric>
                              );
                            })()}
                          </SubmissionDetails>
                        </SubmissionInfo>
                        <SubmissionActions className="submission-actions">
                          <div
                            className="submission-status-dropdown"
                            onClick={(e) => e.stopPropagation()}
                            style={{ minWidth: '180px' }}
                          >
                            <InputDropdown
                              size="compact"
                              id={`submission-status-${group.submissionID}`}
                              label="Status"
                              value={submissionStatus}
                              onChange={(value) => {
                                // Validate that all cards have grades before allowing Complete status
                                if (value === PSA_STATUSES.COMPLETE) {
                                  // Check if submission has no cards
                                  if (group.cards.length === 0) {
                                    dispatch(setSystemNotification('All cards must have a grade to mark as Complete'));
                                    return; // Prevent status update
                                  }
                                  
                                  // Check if any cards are missing grades
                                  const cardsWithoutGrades = group.cards.filter(card => !card.grade || !card.grade.trim());
                                  if (cardsWithoutGrades.length > 0) {
                                    dispatch(setSystemNotification('All cards must have a grade to mark as Complete'));
                                    return; // Prevent status update
                                  }
                                }
                                
                                if (onUpdateSubmission) {
                                  onUpdateSubmission(group.submissionID, { status: value });
                                }
                              }}
                              options={statusOptions}
                              placeholder="Select status"
                            />
                          </div>
                          <PrimaryBtn
                            title="+ ADD CARD"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onAddCardToSubmission) {
                                onAddCardToSubmission(group.submissionID);
                              }
                            }}
                          />
                        </SubmissionActions>
                      </SubmissionHeaderContent>
                    </SubmissionHeader>

                    {/* Expanded submission edit form */}
                    {isExpanded && (
                      <AnimatePresence>
                        <ExpandedSection
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          onClick={(e) => e.stopPropagation()}
                          className="submission-edit"
                        >
                          <SubmissionExpandedContainer>
                            <SubmissionFormColumn>
                              <FormSection>
                                <FormRowContainer>
                                  <FormRow>
                                    <InputBar
                                      type="text"
                                      value={submissionFormData.psaSubmissionID}
                                      updateValue={(value) => setSubmissionFormData(prev => ({ ...prev, psaSubmissionID: value }))}
                                      id={`submission-id-${group.submissionID}`}
                                      label="PSA Submission ID"
                                      pH="Enter PSA Submission ID"
                                    />
                                  </FormRow>

                                  <FormRow>
                                    <InputDropdown
                                      id={`submission-service-${group.submissionID}`}
                                      label="Service Level"
                                      value={submissionFormData.serviceLevel ? formatServiceWithPrice(submissionFormData.serviceLevel) : ''}
                                      onChange={(value) => {
                                        const sortedServices = [...GRADING_SERVICES].sort((a, b) => b.length - a.length);
                                        const extractedService = sortedServices.find(service =>
                                          value.startsWith(service)
                                        ) || value.split(' (')[0];
                                        setSubmissionFormData(prev => ({ ...prev, serviceLevel: extractedService }));
                                      }}
                                      options={submissionServiceLevelOptions}
                                      placeholder="Select service level"
                                    />
                                  </FormRow>
                                </FormRowContainer>

                                {/* Show date/time picker when status is PSA Drop-Off */}
                                {submissionFormData.status === PSA_STATUSES.PSA_DROP_OFF && (
                                  <FormRow>
                                    <FormLabel>Drop-Off Date & Time</FormLabel>
                                    <input
                                      type="datetime-local"
                                      value={submissionFormData.psaDropOffDateTime}
                                      onChange={(e) => setSubmissionFormData(prev => ({ ...prev, psaDropOffDateTime: e.target.value }))}
                                      style={{
                                        width: '100%',
                                        maxWidth: '300px',
                                        padding: '12px',
                                        borderRadius: '4px',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        color: '#FFFFFF',
                                        fontWeight: '600',
                                      }}
                                    />
                                  </FormRow>
                                )}

                                {/* Show tracking number when status is Shipped to PSA */}
                                {submissionFormData.status === PSA_STATUSES.SHIPPED_TO_PSA && (
                                  <FormRow>
                                    <InputBar
                                      type="text"
                                      value={submissionFormData.trackingNumber}
                                      updateValue={(value) => setSubmissionFormData(prev => ({ ...prev, trackingNumber: value }))}
                                      id={`submission-tracking-${group.submissionID}`}
                                      label="Tracking Number"
                                      pH="Enter tracking number"
                                    />
                                  </FormRow>
                                )}

                                {/* Show received date picker when status is Received by PSA */}
                                {submissionFormData.status === PSA_STATUSES.ORDER_ARRIVED && (
                                  <FormRow>
                                    <div>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <FormLabel>Received Date</FormLabel>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                          <input
                                            type="date"
                                            value={submissionFormData.receivedDate}
                                            onChange={(e) => setSubmissionFormData(prev => ({ ...prev, receivedDate: e.target.value }))}
                                            style={{
                                              width: '100%',
                                              maxWidth: '300px',
                                              padding: '12px',
                                              borderRadius: '4px',
                                              background: 'rgba(255, 255, 255, 0.1)',
                                              border: '1px solid rgba(255, 255, 255, 0.2)',
                                              color: '#FFFFFF',
                                              fontWeight: '600'
                                            }}
                                          />
                                          {submissionFormData.receivedDate && (() => {
                                            const receivedDateObj = new Date(submissionFormData.receivedDate);
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            receivedDateObj.setHours(0, 0, 0, 0);
                                            const diffTime = today - receivedDateObj;
                                            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                            return (
                                              <CardAttribute
                                                text={diffDays === 0 ? 'Today' : diffDays === 1 ? '1 day ago' : `${diffDays} days ago`}
                                              />
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </FormRow>
                                )}

                                {/* Show grading start date picker when status is Grading in Progress */}
                                {submissionFormData.status === PSA_STATUSES.GRADING_IN_PROGRESS && (
                                  <FormRow>
                                    <div>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <FormLabel>Grading Start Date</FormLabel>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                          <input
                                            type="date"
                                            value={submissionFormData.gradingStartDate}
                                            onChange={(e) => setSubmissionFormData(prev => ({ ...prev, gradingStartDate: e.target.value }))}
                                            style={{
                                              width: '100%',
                                              maxWidth: '300px',
                                              padding: '12px',
                                              borderRadius: '4px',
                                              background: 'rgba(255, 255, 255, 0.1)',
                                              border: '1px solid rgba(255, 255, 255, 0.2)',
                                              color: '#FFFFFF',
                                              fontWeight: '600'
                                            }}
                                          />
                                          {submissionFormData.gradingStartDate && (() => {
                                            const gradingStartDateObj = new Date(submissionFormData.gradingStartDate);
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            gradingStartDateObj.setHours(0, 0, 0, 0);
                                            const diffTime = today - gradingStartDateObj;
                                            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                            return (
                                              <CardAttribute
                                                text={diffDays === 0 ? 'Today' : diffDays === 1 ? '1 day ago' : `${diffDays} days ago`}
                                              />
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </FormRow>
                                )}
                              </FormSection>

                              <ButtonGroup>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                  <PrimaryBtn
                                    title={isSavingSubmission ? 'Saving...' : 'Save Changes'}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSaveSubmission();
                                    }}
                                    disabled={isSavingSubmission || showDeleteSubmissionConfirm}
                                  />
                                  {!showDeleteSubmissionConfirm ? (
                                    <>
                                      <PrimaryBtn
                                        type="critical"
                                        title="Delete Submission"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteSubmissionClick();
                                        }}
                                        disabled={isSavingSubmission || isDeleting}
                                      />
                                      <SecondaryBtn
                                        title="CANCEL"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedSubmissionId(null);
                                          setShowDeleteSubmissionConfirm(false);
                                        }}
                                        isDisabled={isSavingSubmission}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <PrimaryBtn
                                        type="critical"
                                        title={isDeleting ? 'Deleting...' : 'Confirm Delete'}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteSubmissionConfirm();
                                        }}
                                        disabled={isDeleting}
                                        isLoading={isDeleting}
                                      />
                                      <SecondaryBtn
                                        title="CANCEL"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteSubmissionCancel();
                                        }}
                                        isDisabled={isDeleting}
                                      />
                                    </>
                                  )}
                                </div>
                              </ButtonGroup>
                            </SubmissionFormColumn>

                            <SubmissionCardsColumn>
                              {/* Grading dashboard and Complete button above cards */}
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '12px', flexWrap: 'wrap' }}>
                                <GradingDashboard>
                                  <DashboardMetric>
                                    <MetricLabel>Progress</MetricLabel>
                                    <MetricValue>{cardsWithGrades}/{totalCards}</MetricValue>
                                  </DashboardMetric>
                                  <DashboardMetric>
                                    <MetricLabel>Gem Rate</MetricLabel>
                                    <MetricValue>{gemRate}%</MetricValue>
                                  </DashboardMetric>
                                  <DashboardMetric>
                                    <MetricLabel>Avg Grade</MetricLabel>
                                    <MetricValue>{averageGrade}</MetricValue>
                                  </DashboardMetric>
                                </GradingDashboard>
                                <PrimaryBtn
                                  title="COMPLETE SUBMISSION"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onUpdateSubmission && allCardsGraded) {
                                      onUpdateSubmission(group.submissionID, { status: PSA_STATUSES.COMPLETE });
                                    }
                                  }}
                                  disabled={!allCardsGraded}
                                  style={{
                                    opacity: allCardsGraded ? 1 : 0.5,
                                    cursor: allCardsGraded ? 'pointer' : 'not-allowed'
                                  }}
                                />
                              </div>
                              {group.cards.map(renderCard)}
                            </SubmissionCardsColumn>
                          </SubmissionExpandedContainer>
                        </ExpandedSection>
                      </AnimatePresence>
                    )}
                  </SubmissionGroup>
                );
              })}

              {/* Render unassigned cards */}
              {filteredUnassigned.length > 0 && (
                <UnassignedGroup>
                  <UnassignedHeader>
                    <UnassignedTitle>Unassigned Cards</UnassignedTitle>
                  </UnassignedHeader>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {filteredUnassigned.map(renderCard)}
                  </div>
                </UnassignedGroup>
              )}
            </CardsDisplayContainer>
          );
        })()}
      </CardsContainer>
    </PSACardsDisplayContainer>
  );
}
